import { ActivatedRouteSnapshot, Router } from '@angular/router';
import { Injectable } from '@angular/core';
import { createEffect, Actions, ofType } from '@ngrx/effects';
import { DataPersistence } from '@nrwl/angular';
import { grabIdFromParams } from 'src/shared/utilities/utility-functions';
import { CustomerClient, JobsClient, PersonnelClient } from 'src/shared/api';
import {
	map,
	withLatestFrom,
	mapTo,
	filter,
	concatMap,
	tap,
	switchMap,
} from 'rxjs/operators';
import { Store, select } from '@ngrx/store';
import { CustomerDetailComponent } from '../customer-detail.component';
import { CustomerDetailPartialState } from './customer-detail.reducer';
import { customersFragments } from '../../customers.routes';
import { customerFormToCustomer } from '../customer-detail.utility';
import * as CustomerDetailActions from './customer-detail.actions';
import * as CustomerDetailSelectors from './customer-detail.selectors';
import * as QuoteDetailActions from 'src/admin-app/quotes/quote-detail/+state/quote-detail.actions';
import * as JobDetailActions from 'src/admin-app/jobs/job-detail/+state/job-detail.actions';
import { MatSnackBar } from '@angular/material/snack-bar';
import { notifyAfterSaving } from 'src/shared/utilities/custom-rxjs-operators';
import { getAllListArgs } from 'src/shared/constants';

@Injectable()
export class CustomerDetailEffects {
	selectCustomer$ = createEffect(() =>
		this.dataPersistence.navigation(CustomerDetailComponent, {
			run: (
				r: ActivatedRouteSnapshot,
				_state: CustomerDetailPartialState
			) => {
				const customerId = grabIdFromParams(r.paramMap);

				return CustomerDetailActions.customerSelected({ customerId });
			},
			onError: (_a: ActivatedRouteSnapshot, error: any) => {
				throw new Error(error);
			},
		})
	);

	loadCustomerFromStore$ = createEffect(() =>
		this.actions$.pipe(
			ofType(CustomerDetailActions.customerSelected),
			map((action) => action.customerId),
			filter((id) => id > 0),
			withLatestFrom(
				this.store.pipe(
					select(CustomerDetailSelectors.getSelectedCustomer)
				),
				(_id, customer) => customer
			),
			filter(Boolean),
			map((customer) =>
				CustomerDetailActions.customerLoadedFromStore({ customer })
			)
		)
	);

	loadCustomerFromApi$ = createEffect(() =>
		this.actions$.pipe(
			ofType(
				CustomerDetailActions.customerSelected,
				QuoteDetailActions.customerSelected,
				QuoteDetailActions.customerRequested,
				JobDetailActions.customerSelected,
				JobDetailActions.customerRequested
			),
			map((action) => action.customerId),
			filter((id) => id > 0),
			withLatestFrom(
				this.store.pipe(
					select(CustomerDetailSelectors.getSelectedCustomer)
				)
			),
			filter(([_id, customer]) => !customer),
			concatMap(([id, _customer]) =>
				this.customerClient.getCustomer(id).pipe(
					map((customer) =>
						CustomerDetailActions.customerLoadedFromApi({
							customer,
						})
					)
				)
			)
		)
	);

	loadRailroads$ = createEffect(() =>
		this.actions$.pipe(
			ofType(CustomerDetailActions.customerSelected),
			map((action) => action.customerId),
			filter((id) => id > 0),
			switchMap((id) =>
				this.customerClient
					.getRailroads({ ...getAllListArgs, customerId: id })
					.pipe(
						map((pagedResults) => pagedResults.results),
						map((railroads) =>
							CustomerDetailActions.railroadsLoaded({
								railroads,
							})
						)
					)
			)
		)
	);

	loadRwics$ = createEffect(() =>
		this.actions$.pipe(
			ofType(CustomerDetailActions.customerSelected),
			map((action) => action.customerId),
			filter((id) => id > 0),
			switchMap((id) =>
				this.personnelClient
					.getFieldWorkers({ ...getAllListArgs, customerId: id })
					.pipe(
						map((pagedResults) => pagedResults.results),
						map((rwics) =>
							CustomerDetailActions.rwicsLoaded({
								rwics,
							})
						)
					)
			)
		)
	);

	loadJobs$ = createEffect(() =>
		this.actions$.pipe(
			ofType(CustomerDetailActions.customerSelected),
			map((action) => action.customerId),
			filter((id) => id > 0),
			switchMap((id) =>
				this.jobsClient
					.getJobs({ ...getAllListArgs, customerId: id })
					.pipe(
						map((pagedResults) => pagedResults.results),
						map((jobs) =>
							CustomerDetailActions.jobsLoaded({
								jobs,
							})
						)
					)
			)
		)
	);

	startNewCustomer$ = createEffect(() =>
		this.actions$.pipe(
			ofType(CustomerDetailActions.customerSelected),
			map((action) => action.customerId),
			filter((id) => id === 0),
			mapTo(CustomerDetailActions.newCustomerStarted())
		)
	);

	saveCustomer$ = createEffect(() =>
 		this.dataPersistence.pessimisticUpdate(
    		CustomerDetailActions.saveRequested,
        	{
            	run: (
            	    action: ReturnType<typeof CustomerDetailActions.saveRequested>,
            	    _state: CustomerDetailPartialState
            	) => {
                	const customer = customerFormToCustomer(action.formValue);

    	            return this.customerClient.saveCustomer(customer).pipe(
        	            concatMap((id) =>
            	            this.customerClient.getCustomer(
                	            customer.id === 0 ? id : customer.id
                    	    )
                    	),
                    	map((savedCustomer) =>
                        	customer.id === 0
                        	    ? CustomerDetailActions.newCustomerSaved({
                        	          customer: savedCustomer,
                        	      })
                        	    : CustomerDetailActions.customerSaved({
                        	          customer: savedCustomer,
                        	      })
                    	)
                	);
            	},
            	onError: (
            	    _action: ReturnType<typeof CustomerDetailActions.saveRequested>,
            	    error: any
            	) => {
            	    return CustomerDetailActions.errorSavingCustomer({ error });
            	},
        	}
    	)
	);

	throwIfError$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(
					CustomerDetailActions.errorSavingCustomer,
					CustomerDetailActions.errorLoadingCustomer
				),
				map((action) => action.error),
				map((error) => {
					throw Error(error);
				})
			),
		{ dispatch: false }
	);

	closeAfterSaving$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(CustomerDetailActions.customerSaved),
				withLatestFrom(
					this.store.pipe(
						select(CustomerDetailSelectors.getCloseAfterSaving)
					),
					(_, shouldClose) => shouldClose
				),
				filter(Boolean),
				tap(() => this.router.navigate(customersFragments.list))
			),
		{ dispatch: false }
	);

	navigateToCreatedCustomer$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(CustomerDetailActions.newCustomerSaved),
				map((action) => action.customer.id),
				tap((id) => this.router.navigate(customersFragments.detail(id)))
			),
		{ dispatch: false }
	);

	notifyAfterSaving$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(CustomerDetailActions.customerSaved),
				notifyAfterSaving(this.snackbar)
			),
		{ dispatch: false }
	);

	constructor(
		private readonly actions$: Actions,
		private readonly store: Store<CustomerDetailPartialState>,
		private readonly dataPersistence: DataPersistence<
			CustomerDetailPartialState
		>,
		private readonly customerClient: CustomerClient,
		private readonly jobsClient: JobsClient,
		private readonly personnelClient: PersonnelClient,
		private readonly router: Router,
		private readonly snackbar: MatSnackBar
	) {}
}
